// =============================================================================
// Exception styling - colore + icona per motivo di assenza
// =============================================================================
// Il motivo di un'assenza è una "causale" (AbsenceReason) personalizzabile
// per organizzazione: porta con sé colore e nome icona. `getReasonVisual`
// risolve colore+icona dalla causale, con fallback a un default neutro quando
// la causale manca (es. presenza aggiuntiva). I colori sono applicati a bassa
// opacità sulla griglia per un effetto neutro.

import {
  Palmtree,
  Thermometer,
  PartyPopper,
  Wrench,
  GraduationCap,
  Users,
  CalendarOff,
  Plane,
  Baby,
  Stethoscope,
  Coffee,
  Home,
  Briefcase,
  Ban,
  Clock,
  AlertTriangle,
  Sun,
  Snowflake,
  Heart,
  Star,
  type LucideIcon,
} from 'lucide-react';

export interface ExceptionVisual {
  color: string; // hex — bordo/retino (a bassa opacità) e testo della chip
  Icon: LucideIcon;
}

/**
 * Registry delle icone selezionabili per una causale. Deve restare allineato
 * con `ALLOWED_ABSENCE_ICONS` lato backend
 * (backend/src/common/constants/absence-reasons.constants.ts).
 */
export const ICON_REGISTRY: Record<string, LucideIcon> = {
  CalendarOff,
  Palmtree,
  Thermometer,
  PartyPopper,
  Wrench,
  GraduationCap,
  Users,
  Plane,
  Baby,
  Stethoscope,
  Coffee,
  Home,
  Briefcase,
  Ban,
  Clock,
  AlertTriangle,
  Sun,
  Snowflake,
  Heart,
  Star,
};

/** Nomi icona disponibili nel picker (ordine di presentazione). */
export const ALLOWED_ICON_NAMES = Object.keys(ICON_REGISTRY);

/** Risolve un nome icona nel componente lucide, con fallback su CalendarOff. */
export function resolveIcon(name?: string | null): LucideIcon {
  return (name && ICON_REGISTRY[name]) || CalendarOff;
}

const DEFAULT_VISUAL: ExceptionVisual = { color: '#57534E', Icon: CalendarOff };

/** Sintesi minima di una causale (colore/icona/nome). */
export interface ReasonVisualSource {
  color?: string | null;
  icon?: string | null;
}

/**
 * Colore + icona da usare per un'assenza, ricavati dalla causale collegata.
 * Se la causale manca (es. presenza aggiuntiva) usa un default neutro.
 */
export function getReasonVisual(reason?: ReasonVisualSource | null): ExceptionVisual {
  if (reason && reason.color) {
    return { color: reason.color, Icon: resolveIcon(reason.icon) };
  }
  return DEFAULT_VISUAL;
}

/** Converte un colore hex in rgba con l'alpha indicato. */
export function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
